// src/pages/FindAccount.tsx
import { useState } from "react";
import api from "../lib/api";
import { useNavigate } from "react-router-dom";

type Mode = "id" | "pw";

function extractErrorMessage(e: any, fallback: string) {
  const msg = e?.response?.data?.message;
  if (typeof msg === "string" && msg.trim()) return msg;

  // nginx 405 같은 HTML 응답이 오면 문자열로 들어올 수 있음
  const data = e?.response?.data;
  if (typeof data === "string" && data.includes("405")) {
    return "API가 Nginx에서 차단(405)되고 있습니다. /api 프록시 설정을 확인해 주세요.";
  }
  return fallback;
}

export default function FindAccount() {
  const [mode, setMode] = useState<Mode>("id");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  const resetMessages = () => {
    setMessage(null);
    setError(null);
  };

  async function handleFindId() {
    resetMessages();
    if (!name.trim() || !phone.trim()) {
      setError("이름과 연락처를 입력해 주세요.");
      return;
    }

    try {
      setBusy(true);
      // ✅ baseURL이 /api 이므로 여기서는 /auth 로만 호출
      const res = await api.post("/auth/find-id", {
        name: name.trim(),
        phone: phone.trim(),
      });
      setMessage(res.data?.message || "등록된 아이디를 찾았습니다.");
    } catch (e: any) {
      setError(extractErrorMessage(e, "아이디를 찾는 중 오류가 발생했습니다."));
    } finally {
      setBusy(false);
    }
  }

  async function handleResetPassword() {
    resetMessages();
    if (!email.trim() || !name.trim() || !phone.trim()) {
      setError("이메일, 이름, 연락처를 모두 입력해 주세요.");
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      setError("새 비밀번호는 8자 이상으로 설정해 주세요.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("비밀번호와 비밀번호 확인이 일치하지 않습니다.");
      return;
    }

    try {
      setBusy(true);
      // ✅ baseURL이 /api 이므로 여기서는 /auth 로만 호출
      const res = await api.post("/auth/reset-password", {
        email: email.trim(),
        name: name.trim(),
        phone: phone.trim(),
        newPassword,
      });
      setMessage(res.data?.message || "비밀번호가 재설정되었습니다.");
    } catch (e: any) {
      setError(extractErrorMessage(e, "비밀번호를 재설정하는 중 오류가 발생했습니다."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg px-6 py-7">
        <h1 className="text-xl font-semibold text-slate-900 mb-4 text-center">
          아이디 / 비밀번호 찾기
        </h1>

        <div className="flex rounded-xl bg-slate-100 p-1 mb-5 text-sm">
          <button
            type="button"
            onClick={() => {
              setMode("id");
              resetMessages();
            }}
            className={`flex-1 rounded-lg py-2 font-medium ${
              mode === "id"
                ? "bg-white text-indigo-600 shadow-sm"
                : "text-slate-500"
            }`}
          >
            아이디 찾기
          </button>
          <button
            type="button"
            onClick={() => {
              setMode("pw");
              resetMessages();
            }}
            className={`flex-1 rounded-lg py-2 font-medium ${
              mode === "pw"
                ? "bg-white text-indigo-600 shadow-sm"
                : "text-slate-500"
            }`}
          >
            비밀번호 재설정
          </button>
        </div>

        {mode === "id" ? (
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-700">이름</label>
              <input
                type="text"
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="가입하신 이름을 입력하세요."
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-700">연락처</label>
              <input
                type="tel"
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="예) 01012345678"
              />
            </div>

            {error && (
              <p className="text-xs text-red-600 mt-1 whitespace-pre-line">
                {error}
              </p>
            )}
            {message && (
              <p className="text-xs text-emerald-600 mt-1 whitespace-pre-line">
                {message}
              </p>
            )}

            <button
              type="button"
              onClick={handleFindId}
              disabled={busy}
              className="mt-2 w-full rounded-xl bg-indigo-600 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              {busy ? "조회 중..." : "아이디 찾기"}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-700">
                이메일(아이디)
              </label>
              <input
                type="email"
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="가입하신 이메일을 입력하세요."
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-700">이름</label>
              <input
                type="text"
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="가입하신 이름을 입력하세요."
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-700">연락처</label>
              <input
                type="tel"
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="예) 01012345678"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-700">새 비밀번호</label>
              <input
                type="password"
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="8자 이상 비밀번호"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-700">
                새 비밀번호 확인
              </label>
              <input
                type="password"
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="한 번 더 입력해 주세요."
              />
            </div>

            {error && (
              <p className="text-xs text-red-600 mt-1 whitespace-pre-line">
                {error}
              </p>
            )}
            {message && (
              <p className="text-xs text-emerald-600 mt-1 whitespace-pre-line">
                {message}
              </p>
            )}

            <button
              type="button"
              onClick={handleResetPassword}
              disabled={busy}
              className="mt-2 w-full rounded-xl bg-indigo-600 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              {busy ? "처리 중..." : "비밀번호 재설정"}
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={() => navigate("/login")}
          className="mt-5 w-full text-center text-xs text-slate-500 hover:text-slate-700"
        >
          로그인 화면으로 돌아가기
        </button>
      </div>
    </div>
  );
}
